import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2, Zap, User, Receipt, FileText, ClipboardList, MessageSquare, Paperclip, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';
import { ClienteModal } from '@/components/clientes/ClienteModal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DealSimulatorModal } from '@/components/clientes/DealSimulatorModal';
import { ClienteCadastroTab } from '@/components/clientes/ClienteCadastroTab';
import { ClienteRecebimentosTab } from '@/components/clientes/ClienteRecebimentosTab';
import { ClienteContasReceberTab } from '@/components/clientes/ClienteContasReceberTab';
import { ClienteTarefasTab } from '@/components/clientes/ClienteTarefasTab';
import { ClienteAnotacoesTab } from '@/components/clientes/ClienteAnotacoesTab';
import { ClienteAnexosTab } from '@/components/clientes/ClienteAnexosTab';
import { ClienteHistoricoTab } from '@/components/clientes/ClienteHistoricoTab';

interface Customer {
  id: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  sms_phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  birth_date: string | null;
  notes: string | null;
  cluster: 'bom_pagador' | 'regular' | 'mau_pagador' | null;
  company_id: string;
  assigned_to: string | null;
  collection_rule_id: string | null;
  reminders_active: boolean | null;
  renegotiation_status: string | null;
}

interface Invoice {
  id: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  paid_at: string | null;
}

function maskTaxId(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

const clusterColors: Record<string, string> = {
  bom_pagador: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]',
  regular: 'bg-[hsl(var(--basic-100))] text-[hsl(var(--basic-700))]',
  mau_pagador: 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]'
};

const clusterLabels: Record<string, string> = {
  bom_pagador: 'Bom Pagador',
  regular: 'Regular',
  mau_pagador: 'Inadimplente'
};

const ClienteDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dealModalOpen, setDealModalOpen] = useState(false);

  // Get the page we came from (to handle back navigation properly)
  const fromPage = (location.state as { from?: string })?.from || '/clientes';

  const { data: customer, isLoading } = useQuery({
    queryKey: ['customer', id],
    queryFn: async () => {
      if (!id) throw new Error('ID not provided');
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data as Customer;
    },
    enabled: !!id
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ['customer-invoices', id],
    queryFn: async () => {
      if (!id) return [];
      
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('customer_id', id)
        .order('due_date', { ascending: false });
      
      if (error) throw error;
      return data as Invoice[];
    },
    enabled: !!id
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('ID not provided');
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate('/clientes');
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Cliente não encontrado.</p>
      </div>
    );
  }

  const hasDebt = invoices.some(inv => inv.status === 'pending' || inv.status === 'overdue');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(fromPage)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground truncate">{customer.name}</h1>
            {customer.cluster && (
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase shrink-0 ${clusterColors[customer.cluster]}`}>
                {clusterLabels[customer.cluster]}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground font-mono mt-1">
            {maskTaxId(customer.tax_id)}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {hasDebt && (
            <Button 
              variant="outline"
              onClick={() => setDealModalOpen(true)}
              className="gap-2 border-[hsl(var(--careful-300))] text-[hsl(var(--careful-600))] hover:bg-[hsl(var(--careful-50))] hover:border-[hsl(var(--careful-400))]"
            >
              <Zap className="h-4 w-4" />
              Negociar Dívida
            </Button>
          )}
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setModalOpen(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={() => setConfirmOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="cadastro" className="w-full">
        <TabsList className="flex-wrap h-auto gap-1 bg-muted/50 p-1">
          <TabsTrigger value="cadastro" className="gap-2">
            <User className="h-4 w-4" />
            Cadastro
          </TabsTrigger>
          <TabsTrigger value="recebimentos" className="gap-2">
            <Receipt className="h-4 w-4" />
            Recebimentos
          </TabsTrigger>
          <TabsTrigger value="contas" className="gap-2">
            <FileText className="h-4 w-4" />
            Contas a Receber
          </TabsTrigger>
          <TabsTrigger value="tarefas" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            Tarefas
          </TabsTrigger>
          <TabsTrigger value="anotacoes" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Anotações
          </TabsTrigger>
          <TabsTrigger value="anexos" className="gap-2">
            <Paperclip className="h-4 w-4" />
            Anexos
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-2">
            <History className="h-4 w-4" />
            Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cadastro" className="mt-6">
          <ClienteCadastroTab customer={customer} />
        </TabsContent>

        <TabsContent value="recebimentos" className="mt-6">
          <ClienteRecebimentosTab customerId={customer.id} />
        </TabsContent>

        <TabsContent value="contas" className="mt-6">
          <ClienteContasReceberTab customerId={customer.id} />
        </TabsContent>

        <TabsContent value="tarefas" className="mt-6">
          <ClienteTarefasTab customerId={customer.id} companyId={customer.company_id} />
        </TabsContent>

        <TabsContent value="anotacoes" className="mt-6">
          <ClienteAnotacoesTab customerId={customer.id} companyId={customer.company_id} />
        </TabsContent>

        <TabsContent value="anexos" className="mt-6">
          <ClienteAnexosTab customerId={customer.id} companyId={customer.company_id} />
        </TabsContent>

        <TabsContent value="historico" className="mt-6">
          <ClienteHistoricoTab customerId={customer.id} />
        </TabsContent>
      </Tabs>

      <ClienteModal 
        open={modalOpen}
        onOpenChange={setModalOpen}
        customer={customer}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['customer', id] })}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Excluir Cliente"
        description="Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita."
        onConfirm={() => deleteMutation.mutate()}
        confirmText="Excluir"
        variant="destructive"
      />

      <DealSimulatorModal
        open={dealModalOpen}
        onOpenChange={setDealModalOpen}
        invoices={invoices}
      />
    </div>
  );
};

export default ClienteDetalhe;
